import {
  mapGroupEntityDto,
  mapGroupMembershipDto,
  type GroupEntity,
  type GroupEntityDto,
  type GroupMembership,
  type GroupMembershipDto,
  type ListResultDto,
} from '@/types';

import type {
  AddMemberRequest,
  GroupUpdateRequest,
  GroupsService,
} from '../groupsService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`GroupsService`(Beta v0.0.1)
 * -----------------------------------------------------------------------------
 * group endpoints **不需要 `X-Current-Group` header**(對齊
 * `GROUP_HEADER_EXEMPT_PATTERNS` `/api/groups` prefix exempt;BE 端 L1.5 不掛)。
 *
 * `POST /api/groups`(create)已於 2026-08-10 隨 FE 端 caller 一併移除 —— BE 2026-06-23
 * 就下架了那支端點,留著只是一個保證 404 的方法。
 */

const PATH = {
  list: '/api/groups',
  item: (gid: string) => `/api/groups/${encodeURIComponent(gid)}`,
  members: (gid: string) => `/api/groups/${encodeURIComponent(gid)}/members`,
  member: (gid: string, targetCuid: string) =>
    `/api/groups/${encodeURIComponent(gid)}/members/${encodeURIComponent(targetCuid)}`,
} as const;

function buildUpdateBody(patch: GroupUpdateRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.description !== undefined) body.description = patch.description;
  return body;
}

export function createHttpGroupsService(config: HttpClientConfig): GroupsService {
  return {
    async list(): Promise<GroupEntity[]> {
      // BE 回 `{ items: GroupEntityDto[] }`;對齊 spec §2.2 拉清單格式
      const env = await httpRequestWithRefresh<ListResultDto<GroupEntityDto>>(
        config,
        'GET',
        PATH.list,
      );
      const items = env.result?.items ?? [];
      return items.map(mapGroupEntityDto);
    },

    async get(gid: string): Promise<GroupEntity> {
      const env = await httpRequestWithRefresh<GroupEntityDto>(config, 'GET', PATH.item(gid));
      return mapGroupEntityDto(unwrapResult(env, 'groups.get'));
    },

    async update(gid: string, patch: GroupUpdateRequest): Promise<GroupEntity> {
      const env = await httpRequestWithRefresh<GroupEntityDto>(
        config,
        'PATCH',
        PATH.item(gid),
        buildUpdateBody(patch),
      );
      return mapGroupEntityDto(unwrapResult(env, 'groups.update'));
    },

    async listMembers(gid: string): Promise<GroupMembership[]> {
      // T3(docs/50)— GET 同 members path,BE 回 `{ items: GroupMembershipDto[] }`。
      const env = await httpRequestWithRefresh<ListResultDto<GroupMembershipDto>>(
        config,
        'GET',
        PATH.members(gid),
      );
      const items = env.result?.items ?? [];
      return items.map(mapGroupMembershipDto);
    },

    async addMember(gid: string, req: AddMemberRequest): Promise<GroupMembership> {
      const env = await httpRequestWithRefresh<GroupMembershipDto>(
        config,
        'POST',
        PATH.members(gid),
        { cuid: req.cuid, role: req.role },
      );
      return mapGroupMembershipDto(unwrapResult(env, 'groups.addMember'));
    },

    async removeMember(gid: string, targetCuid: string): Promise<void> {
      await httpRequestWithRefresh(config, 'DELETE', PATH.member(gid, targetCuid));
    },
  };
}
